import React, { useEffect, useState, useCallback } from "react";
import { facilityAdmin } from "../../api";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, inputStyle, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

const CLIP_TYPES = [
  { value: "chime",        label: "Attention Chime",    desc: "Plays before every announcement to get attention",                   example: "ding.mp3" },
  { value: "station_name", label: "Station Name",       desc: "Spoken name of a station — one clip per station",                    example: '"Customer Care Counter"' },
  { value: "number_digit", label: "Number Digit (0–9)", desc: "One clip per digit. System combines them to say ticket numbers",     example: '"Zero", "One", … "Nine"' },
  { value: "connector",    label: "Connector Phrase",   desc: 'Phrase between number and station: "please proceed to"',             example: '"please proceed to"' },
  { value: "welcome",      label: "Welcome Message",    desc: "Plays when kiosk starts up",                                         example: '"Welcome to City Hospital"' },
  { value: "complete",     label: "Completion Message", desc: "Plays when a patient's journey is fully complete",                   example: '"Thank you, your visit is complete"' },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "tw", label: "Twi" },
];

export default function AudioPage() {
  const [assets,  setAssets]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal, setUploadModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [playing, setPlaying] = useState(null); // assetId

  const load = useCallback(() => {
    setLoading(true);
    facilityAdmin.listAudio()
      .then(r => setAssets(r.data?.results || r.data))
      .catch(() => toast.error("Failed to load audio assets."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    setSaving(true);
    try {
      await facilityAdmin.deleteAudio(deleteConfirm.id);
      toast.success("Audio clip deleted.");
      setDeleteConfirm(null); load();
    } catch { toast.error("Failed to delete."); }
    finally  { setSaving(false); }
  };

  const handlePlay = (asset) => {
    if (playing === asset.id) {
      setPlaying(null); return;
    }
    const audio = new Audio(asset.audio_file_url);
    audio.play().catch(() => toast.error("Could not play audio."));
    audio.onended = () => setPlaying(null);
    setPlaying(asset.id);
  };

  // Group by clip_type
  const byType = {};
  CLIP_TYPES.forEach(ct => { byType[ct.value] = assets.filter(a => a.clip_type === ct.value); });

  return (
    <div style={styles.root}>
      <PageHeader
        title="Audio & Sounds"
        subtitle="Upload audio clips for queue announcements. The display board uses these to announce ticket numbers aloud."
        action={<Btn onClick={() => setUploadModal(true)}>+ Upload Clip</Btn>}
      />

      {/* How it works banner */}
      <div style={styles.howItWorks}>
        <span style={styles.howIcon}>🔊</span>
        <div>
          <div style={styles.howTitle}>How announcement audio works</div>
          <div style={styles.howDesc}>
            When a ticket is called, the board plays: <span style={styles.howSeq}>[Chime] → [Ticket number digits] → [Connector phrase] → [Station name]</span>.
            If any clip is missing, the board falls back to browser text-to-speech automatically.
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div style={styles.sections}>
          {CLIP_TYPES.map(ct => (
            <div key={ct.value} style={styles.section}>
              <div style={styles.sectionHead}>
                <div>
                  <div style={styles.sectionTitle}>{ct.label}</div>
                  <div style={styles.sectionDesc}>{ct.desc}</div>
                  <div style={styles.sectionExample}>Example: {ct.example}</div>
                </div>
                <Btn size="sm" variant="ghost" onClick={() => setUploadModal({ clip_type: ct.value })}>
                  + Upload
                </Btn>
              </div>

              {byType[ct.value].length === 0 ? (
                <div style={styles.emptyClip}>
                  No clips uploaded — system will use text-to-speech fallback.
                </div>
              ) : (
                <div style={styles.clipList}>
                  {byType[ct.value].map(asset => (
                    <div key={asset.id} style={styles.clipRow}>
                      <button
                        style={{ ...styles.playBtn, background: playing === asset.id ? "rgba(37,99,235,0.3)" : "var(--surface-border)" }}
                        onClick={() => handlePlay(asset)}
                        title={playing === asset.id ? "Stop" : "Play"}
                      >
                        {playing === asset.id ? "⏹" : "▶"}
                      </button>
                      <div style={styles.clipInfo}>
                        <span style={styles.clipLabel}>{asset.label}</span>
                        <span style={styles.clipLang}>{LANGUAGES.find(l => l.value === asset.language)?.label || asset.language}</span>
                      </div>
                      <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm(asset)}>Delete</Btn>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <UploadModal
        open={!!uploadModal}
        initial={uploadModal !== true ? uploadModal : null}
        onClose={() => setUploadModal(false)}
        onUploaded={() => { setUploadModal(false); load(); }}
      />

      <ConfirmModal
        open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete} loading={saving}
        title="Delete Audio Clip?"
        message={`"${deleteConfirm?.label}" will be permanently deleted.`}
        confirmLabel="Delete" variant="danger"
      />
    </div>
  );
}

function UploadModal({ open, initial, onClose, onUploaded }) {
  const [form, setForm] = useState({ clip_type: "", language: "en", label: "", file: null });
  const [saving, setSaving] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  useEffect(() => {
    if (open) setForm({ clip_type: initial?.clip_type || "", language: "en", label: "", file: null });
  }, [open, initial]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) { toast.error("Please upload an audio file (mp3, wav, ogg)."); return; }
    set("file", file);
  };

  const handleUpload = async () => {
    if (!form.clip_type || !form.label.trim() || !form.file) {
      toast.error("Please fill all fields and select a file."); return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("clip_type", form.clip_type);
      fd.append("language",  form.language);
      fd.append("label",     form.label);
      fd.append("audio_file", form.file);
      await facilityAdmin.uploadAudio(fd);
      toast.success("Audio clip uploaded.");
      onUploaded();
    } catch { toast.error("Upload failed."); }
    finally  { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Upload Audio Clip" width="460px">
      <div style={F.form}>
        <FormField label="Clip Type" required>
          <select style={selectStyle} value={form.clip_type} onChange={e => set("clip_type", e.target.value)}>
            <option value="">— Select type —</option>
            {CLIP_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
        </FormField>

        {form.clip_type && (
          <div style={F.hint}>
            💡 {CLIP_TYPES.find(c => c.value === form.clip_type)?.desc}
          </div>
        )}

        <div style={F.row2}>
          <FormField label="Language" required>
            <select style={selectStyle} value={form.language} onChange={e => set("language", e.target.value)}>
              {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
          </FormField>
          <FormField label="Label / Value" required hint={form.clip_type === "number_digit" ? 'e.g. "0", "1"' : form.clip_type === "station_name" ? "Station spoken name" : "Descriptive label"}>
            <input style={inputStyle} value={form.label} onChange={e => set("label", e.target.value)} placeholder={form.clip_type === "number_digit" ? "0" : "e.g. Customer Care Counter"} />
          </FormField>
        </div>

        {/* Drop zone */}
        <div
          style={{ ...F.dropZone, ...(dragOver ? F.dropZoneActive : {}), ...(form.file ? F.dropZoneDone : {}) }}
          onDragOver={e => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={() => document.getElementById("audio-file-input").click()}
        >
          <input id="audio-file-input" type="file" accept="audio/*" style={{ display: "none" }}
            onChange={e => handleFile(e.target.files[0])} />
          {form.file ? (
            <>
              <span style={{ fontSize: "24px" }}>🎵</span>
              <span style={{ fontSize: "13px", color: "#22C55E", fontWeight: "600" }}>{form.file.name}</span>
              <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>{(form.file.size / 1024).toFixed(1)} KB</span>
            </>
          ) : (
            <>
              <span style={{ fontSize: "24px" }}>🎙️</span>
              <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>Drop audio file here or click to browse</span>
              <span style={{ fontSize: "11px", color: "var(--text-faint)" }}>MP3, WAV, OGG supported</span>
            </>
          )}
        </div>

        <div style={F.footer}>
          <Btn variant="ghost" onClick={onClose} disabled={saving}>Cancel</Btn>
          <Btn onClick={handleUpload} disabled={saving || !form.clip_type || !form.label.trim() || !form.file}>
            {saving ? "Uploading…" : "Upload Clip"}
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root: { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "900px" },
  howItWorks: {
    display: "flex", gap: "14px", alignItems: "flex-start",
    background: "rgba(37,99,235,0.07)", border: "1px solid rgba(37,99,235,0.2)",
    borderRadius: "12px", padding: "16px 18px", marginBottom: "24px",
  },
  howIcon:  { fontSize: "22px", flexShrink: 0 },
  howTitle: { fontSize: "13px", fontWeight: "700", color: "#60A5FA", marginBottom: "4px" },
  howDesc:  { fontSize: "13px", color: "var(--text-secondary)", lineHeight: "1.5" },
  howSeq:   { color: "var(--text-primary)", fontFamily: "monospace", fontSize: "12px" },
  sections: { display: "flex", flexDirection: "column", gap: "12px" },
  section: {
    background: "var(--surface-bg)", border: "1px solid var(--surface-border)",
    borderRadius: "12px", overflow: "hidden",
  },
  sectionHead: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    gap: "12px", padding: "14px 18px", borderBottom: "1px solid rgba(37,99,235,0.08)",
    background: "rgba(37,99,235,0.03)",
  },
  sectionTitle:  { fontSize: "14px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "2px" },
  sectionDesc:   { fontSize: "12px", color: "var(--text-secondary)", lineHeight: "1.4" },
  sectionExample:{ fontSize: "11px", color: "var(--text-faint)", marginTop: "3px", fontFamily: "monospace" },
  emptyClip:     { padding: "14px 18px", fontSize: "12px", color: "var(--text-faint)", fontStyle: "italic" },
  clipList:      { padding: "8px 0" },
  clipRow: {
    display: "flex", alignItems: "center", gap: "12px",
    padding: "10px 18px", borderBottom: "1px solid rgba(37,99,235,0.05)",
  },
  playBtn: {
    width: "32px", height: "32px", borderRadius: "8px",
    border: "none", cursor: "pointer", fontSize: "14px",
    color: "#60A5FA", display: "flex", alignItems: "center", justifyContent: "center",
    flexShrink: 0, transition: "background 0.15s",
  },
  clipInfo: { flex: 1, display: "flex", alignItems: "center", gap: "10px" },
  clipLabel:{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" },
  clipLang: { fontSize: "11px", color: "var(--text-muted)", background: "var(--input-border)", borderRadius: "4px", padding: "2px 6px" },
};

const F = {
  form:  { display: "flex", flexDirection: "column", gap: "14px" },
  row2:  { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" },
  footer:{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingTop: "8px", borderTop: "1px solid var(--surface-border)", marginTop: "4px" },
  hint:  { background: "rgba(37,99,235,0.07)", borderRadius: "8px", padding: "10px 12px", fontSize: "12px", color: "var(--text-secondary)" },
  dropZone: {
    border: "2px dashed rgba(37,99,235,0.3)", borderRadius: "10px",
    padding: "28px", display: "flex", flexDirection: "column",
    alignItems: "center", gap: "6px", cursor: "pointer",
    transition: "border-color 0.15s, background 0.15s",
    textAlign: "center",
  },
  dropZoneActive: { borderColor: "#2563EB", background: "rgba(37,99,235,0.08)" },
  dropZoneDone:   { borderColor: "#22C55E", background: "rgba(34,197,94,0.05)" },
};
